import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'

import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import {
  MatterArtifactEditOperation,
  MatterArtifactVersionReadSchema,
  MatterArtifactVersionRevisionKind,
  MatterArtifactVersionStatus,
  type MatterArtifactVersionRead,
} from '@/matter-artifacts/schemas'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

const kSelectedPassageEditsPath = '/api/v2/matter-artifact/selected-passage-edits'

export type AcceptAgentV2MatterArtifactEditRequest = {
  proposal_id: string
  originating_turn_id: string
  source_matter_artifact_version_id: string
  operation: MatterArtifactEditOperation
  replacement_markdown: string
}

export async function postAcceptAgentV2MatterArtifactEdit(
  request: AcceptAgentV2MatterArtifactEditRequest,
  expectedMatterArtifactId: string
): Promise<MatterArtifactVersionRead> {
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw new Error("Could not retrieve user's authentication token.")

  const url = `${kPaxtonAppApiRESTBaseUrl()}${kSelectedPassageEditsPath}`
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json; charset=UTF-8',
      'X-Transaction-Id': nanoid(),
    },
    body: JSON.stringify(request),
  })
  const { data, parseError } = await readResponseJson(response)

  if (!response.ok) {
    throw new ApiError(response.status, getErrorMessage(data) || `Failed to accept Matter artifact edit: ${response.status}`, getErrorDetail(data))
  }

  if (parseError) {
    Sentry.captureException(new Error('Matter artifact selected-passage edit response JSON parse failed'), {
      extra: {
        ...getResponseDebugContext(response, url),
        proposalId: request.proposal_id,
        originatingTurnId: request.originating_turn_id,
        sourceMatterArtifactVersionId: request.source_matter_artifact_version_id,
        operation: request.operation,
        parseError: formatUnknownError(parseError),
      },
    })
    throw new ApiResponsePayloadError(response.status, 'Invalid Matter artifact selected-passage edit response JSON')
  }

  const parsed = MatterArtifactVersionReadSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.captureException(new Error('Matter artifact selected-passage edit response schema validation failed'), {
      extra: {
        ...getResponseDebugContext(response, url),
        proposalId: request.proposal_id,
        originatingTurnId: request.originating_turn_id,
        sourceMatterArtifactVersionId: request.source_matter_artifact_version_id,
        operation: request.operation,
        responseKeys: getTopLevelKeys(data),
        issues: parsed.error.issues,
      },
    })
    throw new ApiResponsePayloadError(response.status, 'Invalid Matter artifact selected-passage edit response shape', parsed.error.issues)
  }

  if (
    parsed.data.id !== request.proposal_id ||
    parsed.data.matter_artifact_id !== expectedMatterArtifactId ||
    parsed.data.parent_matter_artifact_version_id !== request.source_matter_artifact_version_id ||
    parsed.data.revision_kind !== MatterArtifactVersionRevisionKind.SELECTED_PASSAGE_EDIT ||
    parsed.data.selected_passage_edit_operation !== request.operation ||
    parsed.data.status !== MatterArtifactVersionStatus.COMPLETE
  ) {
    Sentry.captureException(new Error('Matter artifact selected-passage edit response did not match request'), {
      extra: {
        ...getResponseDebugContext(response, url),
        proposalId: request.proposal_id,
        originatingTurnId: request.originating_turn_id,
        sourceMatterArtifactVersionId: request.source_matter_artifact_version_id,
        operation: request.operation,
        resultMatterArtifactVersionId: parsed.data.id,
        resultMatterArtifactId: parsed.data.matter_artifact_id,
        resultParentMatterArtifactVersionId: parsed.data.parent_matter_artifact_version_id,
        resultRevisionKind: parsed.data.revision_kind,
        resultOperation: parsed.data.selected_passage_edit_operation,
        resultStatus: parsed.data.status,
      },
    })
    throw new ApiResponsePayloadError(response.status, 'Matter artifact selected-passage edit response did not match the accepted proposal')
  }

  return parsed.data
}

function getErrorDetail(data: unknown): unknown {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  return (data as { detail?: unknown }).detail ?? null
}

function getErrorMessage(data: unknown): string | null {
  const detail = getErrorDetail(data)
  if (typeof detail === 'string') return detail
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null
  const message = (detail as { message?: unknown }).message
  return typeof message === 'string' ? message : null
}
